/**
 * useCorrelation – Correlation scanner & pair trading store (Zustand).
 */
import { create } from 'zustand'
import {
  getCorrelationMatrix,
  getCorrelatedPairs,
  getPairDivergence,
  getCorrelationSectorMatrix,
} from '@/lib/api'
import type {
  CorrelationMatrixResult,
  CorrelatedPairsResult,
  PairDivergenceResult,
  SectorCorrelationResult,
} from '@/lib/types'

interface CorrelationState {
  // Matrix
  matrix: CorrelationMatrixResult | null
  matrixLoading: boolean
  matrixError: string | null

  // Pairs
  pairs: CorrelatedPairsResult | null
  pairsLoading: boolean
  pairsError: string | null

  // Divergence
  divergence: PairDivergenceResult | null
  divergenceLoading: boolean
  divergenceError: string | null

  // Sector
  sectorMatrix: SectorCorrelationResult | null
  sectorLoading: boolean
  sectorError: string | null

  // Settings
  tickers: string
  period: string
  threshold: number
  pairTicker1: string
  pairTicker2: string

  setTickers: (t: string) => void
  setPeriod: (p: string) => void
  setThreshold: (t: number) => void
  setPairTickers: (t1: string, t2: string) => void

  fetchMatrix: () => Promise<void>
  fetchPairs: () => Promise<void>
  fetchDivergence: (t1: string, t2: string) => Promise<void>
  fetchSectorMatrix: () => Promise<void>
}

export const useCorrelation = create<CorrelationState>()((set, get) => ({
  matrix: null,
  matrixLoading: false,
  matrixError: null,

  pairs: null,
  pairsLoading: false,
  pairsError: null,

  divergence: null,
  divergenceLoading: false,
  divergenceError: null,

  sectorMatrix: null,
  sectorLoading: false,
  sectorError: null,

  tickers: 'AAPL,MSFT,GOOGL,AMZN,NVDA',
  period: '6m',
  threshold: 0.85,
  pairTicker1: 'AAPL',
  pairTicker2: 'MSFT',

  setTickers: (tickers) => set({ tickers }),
  setPeriod: (period) => set({ period }),
  setThreshold: (threshold) => set({ threshold }),
  setPairTickers: (pairTicker1, pairTicker2) => set({ pairTicker1, pairTicker2 }),

  fetchMatrix: async () => {
    const { tickers, period } = get()
    set({ matrixLoading: true, matrixError: null })
    try {
      const matrix = await getCorrelationMatrix(tickers, period)
      set({ matrix, matrixLoading: false })
    } catch (e) {
      set({ matrixError: String(e), matrixLoading: false })
    }
  },

  fetchPairs: async () => {
    const { threshold, period } = get()
    set({ pairsLoading: true, pairsError: null })
    try {
      const pairs = await getCorrelatedPairs('sp50', threshold, period)
      set({ pairs, pairsLoading: false })
    } catch (e) {
      set({ pairsError: String(e), pairsLoading: false })
    }
  },

  fetchDivergence: async (t1: string, t2: string) => {
    const { period } = get()
    set({ divergenceLoading: true, divergenceError: null, pairTicker1: t1, pairTicker2: t2 })
    try {
      const divergence = await getPairDivergence(t1, t2, period)
      set({ divergence, divergenceLoading: false })
    } catch (e) {
      set({ divergenceError: String(e), divergenceLoading: false })
    }
  },

  fetchSectorMatrix: async () => {
    const { period } = get()
    set({ sectorLoading: true, sectorError: null })
    try {
      const sectorMatrix = await getCorrelationSectorMatrix(period)
      set({ sectorMatrix, sectorLoading: false })
    } catch (e) {
      set({ sectorError: String(e), sectorLoading: false })
    }
  },
}))
